import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Paper, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { downloadBlob } from "../../../components/staffing/downloadScheduleUtils";
import { CreateMissingUsersButton } from "../../../components/team/CreateMissingUsersButton/CreateMissingUsersButton";
import { SystemRoundedCheckbox } from "../../../components/UIToolkit/Clickable/SystemRoundedCheckbox";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useCalculateRecurringShifts, useCleanSchedule, useGenerateOnboardingReport } from "../hooks";

export function DbActionsSection() {
	const { chosenDepartment } = useHospitalData();
	const { showSnackbar } = useSnackbar();
	const { mutateAsync: performCalculateRecurringShifts, isPending: calculateRecurringShiftsLoading } = useCalculateRecurringShifts();
	const { mutateAsync: performCleanSchedule, isPending: cleanScheduleLoading } = useCleanSchedule();
	const { mutateAsync: performGenerateReport, isPending: generateReportLoading } = useGenerateOnboardingReport();
	const [deleteFromDate, setDeleteFromDate] = useState<string>("");
	const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
	const [confirmationText, setConfirmationText] = useState("");
	const [cleanScheduleTemplate, setCleanScheduleTemplate] = useState(false);

	const handleCalculateRecurring = async () => {
		if (!chosenDepartment?.department_id) {
			showSnackbar?.("Please select a department", "error");
			return;
		}

		try {
			const data = await performCalculateRecurringShifts(chosenDepartment.department_id);
			const blob = new Blob([data], {
				type: "text/csv;charset=utf-8;",
			});
			const filename = `calculated_recurring_shifts_${chosenDepartment.department_id}_${new Date().toISOString().split("T")[0]}.csv`;
			downloadBlob(blob, filename);
			showSnackbar?.("Recurring shifts calculated and downloaded", "success");
		} catch (error) {
			console.error("Calculate recurring error:", error);
		}
	};

	const handleGenerateReport = async () => {
		if (!chosenDepartment?.department_id) {
			showSnackbar?.("Please select a department", "error");
			return;
		}

		try {
			const data = await performGenerateReport(chosenDepartment.department_id);
			const blob = new Blob([data], {
				type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
			});
			const deptName = chosenDepartment.display_name || chosenDepartment.name || chosenDepartment.department_id;
			const filename = `initial_data_load_report_${deptName}_${new Date().toISOString().split("T")[0]}.xlsx`;
			downloadBlob(blob, filename);
			showSnackbar?.("Onboarding report downloaded", "success");
		} catch (error) {
			console.error("Generate report error:", error);
		}
	};

	const handleCleanScheduleClick = () => {
		setConfirmDialogOpen(true);
		setConfirmationText("");
	};

	const handleConfirmCleanSchedule = async () => {
		if (!chosenDepartment?.department_id) {
			showSnackbar?.("Please select a department", "error");
			setConfirmDialogOpen(false);
			return;
		}

		try {
			await performCleanSchedule({
				departmentId: chosenDepartment.department_id,
				...(deleteFromDate ? { deleteFromDate } : {}),
				cleanScheduleTemplate,
			});
			showSnackbar?.("Schedule cleaned successfully", "success");
			setDeleteFromDate("");
			setConfirmDialogOpen(false);
			setConfirmationText("");
			setCleanScheduleTemplate(false);
		} catch (error) {
			console.error("Clean schedule error:", error);
			setConfirmDialogOpen(false);
		}
	};

	const handleCloseDialog = () => {
		setConfirmDialogOpen(false);
		setConfirmationText("");
		setCleanScheduleTemplate(false);
	};

	return (
		<Paper sx={{ p: 3 }}>
			<Typography variant="h5" sx={{ mb: 2 }}>
				DB Actions
			</Typography>
			<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
				<Button variant="contained" color="success" disabled={generateReportLoading} onClick={handleGenerateReport}>
					{generateReportLoading ? "Generating..." : "Generate Onboarding Report"}
				</Button>

				<CreateMissingUsersButton />

				<Box>
					<Button variant="contained" fullWidth disabled={calculateRecurringShiftsLoading} onClick={handleCalculateRecurring}>
						{calculateRecurringShiftsLoading ? "Calculating..." : "Calculate recurring (CSV export)"}
					</Button>
					<Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
						Downloads the old calculation as a CSV and creates nothing. To propose patterns, review them per employee and create the templates, use the
						<strong> Calculate Recurring</strong> tab.
					</Typography>
				</Box>

				<TextField
					label="Delete shifts from this date onward (optional - leave empty to delete all)"
					type="date"
					value={deleteFromDate}
					onChange={(e) => setDeleteFromDate(e.target.value)}
					fullWidth
					slotProps={{
						inputLabel: { shrink: true },
					}}
				/>

				<Button variant="contained" disabled={cleanScheduleLoading} onClick={handleCleanScheduleClick} color="error">
					{cleanScheduleLoading ? "Cleaning..." : "Clean schedule"}
				</Button>
			</Box>
			<Dialog open={confirmDialogOpen} onClose={handleCloseDialog}>
				<DialogTitle>Confirm Clean Schedule</DialogTitle>
				<DialogContent>
					<DialogContentText sx={{ mb: 2 }}>
						This action will delete all shifts{deleteFromDate ? ` starting on or after ${deleteFromDate}` : ""}. This cannot be undone.
						{cleanScheduleTemplate ? (
							<> All schedule template / location rows (and their ratio ladders, rooms, and location credentials) will also be permanently deleted.</>
						) : null}
						<br />
						<br />
						Please type <strong>"clean schedule"</strong> to confirm:
					</DialogContentText>
					<SystemRoundedCheckbox
						sx={{ mb: 2 }}
						data-testid="clean-schedule-template-checkbox"
						checked={cleanScheduleTemplate}
						onChange={setCleanScheduleTemplate}
						label="Also delete schedule template rows (locations)"
					/>
					<TextField
						autoFocus
						fullWidth
						value={confirmationText}
						onChange={(e) => setConfirmationText(e.target.value)}
						placeholder="clean schedule"
						variant="outlined"
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleCloseDialog}>Cancel</Button>
					<Button
						onClick={handleConfirmCleanSchedule}
						disabled={confirmationText !== "clean schedule" || cleanScheduleLoading}
						variant="contained"
						color="error"
					>
						{cleanScheduleLoading ? "Cleaning..." : "Submit"}
					</Button>
				</DialogActions>
			</Dialog>
		</Paper>
	);
}
